import { useState, useCallback, useRef, useEffect } from 'react';
import AuditorExport from '@/js/external-js/variableModules/namespaces/AuditorExport/AuditorExport.class';
import { toast } from 'sonner';
import {
    AUDIT_TYPES,
    FRAMEWORKS,
    FrameworkOption,
    DateRange,
    AuditInfo
} from './auditor-export.types.ts';

export const useAuditorExport = (companyID: string) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [frameworks, setFrameworks] = useState<FrameworkOption[]>(() =>
        FRAMEWORKS.map(framework => ({
            ...framework,
            checked: false
        }))
    );

    const [dateRange, setDateRange] = useState<DateRange>({
        startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
    });

    const [auditInfo, setAuditInfo] = useState<AuditInfo>({
        typeOfAudit: '',
        startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
    });

    const auditorExportRef = useRef<AuditorExport | null>(null);

    // Initialize AuditorExport
    useEffect(() => {
        if (!auditorExportRef.current) {
            auditorExportRef.current = new AuditorExport({
                logs: true
            });
        }
    }, []);

    const toggleFramework = useCallback((id: string) => {
        setFrameworks(prev =>
            prev.map(fw =>
                fw.id === id ? { ...fw, checked: !fw.checked } : fw
            )
        );
    }, []);

    const handleDateChange = useCallback((field: 'startDate' | 'endDate', value: string) => {
        setDateRange(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleAuditInfoChange = useCallback((field: keyof typeof auditInfo, value: string) => {
        setAuditInfo(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const exportAuditPack = useCallback(async () => {
        if (!auditorExportRef.current) {
            setError(new Error('Auditor export not initialized'));
            return false;
        }
        const selectedFrameworks = frameworks
            .filter(fw => fw.checked)
            .map(fw => fw.id);

        if (selectedFrameworks.length === 0) {
            setError(new Error('Please select at least one framework'));
            return false;
        }
        try {
            setIsExporting(true);
            setError(null);

            // Prepare form values in the format expected by exportNoUI
            const formValues = {
                selectedFrameworks: frameworks.filter(f => f.checked).map(f => f.id),
                assuranceDateRange: dateRange,
                auditInfo
            };

            return new Promise((resolve) => {
                try {
                    auditorExportRef.current?.exportNoUI(
                        companyID,
                        formValues,
                        (isLoading: boolean) => setIsLoading(isLoading),
                        () => {
                            // onSuccess
                            setIsSuccess(true);
                            resolve(true);
                        },
                        (error: unknown) => {
                            // onError
                            setError(error instanceof Error ? error : new Error(String(error)));
                            resolve(false);
                        }
                    );
                } catch (err) {
                    setError(err instanceof Error ? err : new Error(String(err)));
                    resolve(false);
                }
            });
        } catch (err: unknown) {
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            setIsExporting(false);
        }
    }, [auditorExportRef, companyID, frameworks, dateRange, auditInfo]);

    return {
        isLoading,
        isExporting,
        error,
        frameworks,
        dateRange,
        auditInfo,
        toggleFramework,
        handleDateChange,
        handleAuditInfoChange,
        exportAuditPack,
        isSuccess,
    };
};
